import ComponentManager from '@/Management/ComponentManager';
import ControlViewModelBuilder from '@/Builder/ControlViewModelBuilder';
import { Dictionary, IMessageBoxViewModelConstructorParameter } from '#/DefaultViewExtensionInterfaces';
import type MessageBoxViewModelT from '@/ViewModels/Controls/MessageBoxViewModel';
import ImageViewModelBuilder from '@/Builder/ImageViewModelBuilder';
import LabelViewModelBuilder from '@/Builder/LabelViewModelBuilder';
import RADApplication from '@/Management/RADApplication';
import ExceptionInfoHelper from '@/Components/MessageBox/ExceptionInfo/ExceptionInfoHelper';
import WindowViewModelBuilder from '@/Builder/WindowViewModelBuilder';
import ButtonViewModelBuilder from '@/Builder/ButtonViewModelBuilder';
import Utilities from '@/Utility/Utilities';
import GlobalConfiguration from '@/Management/GlobalConfiguration';

export default class MessageBoxViewModelBuilder<T = null> extends ControlViewModelBuilder<MessageBoxViewModelT, T>{

    private readonly MSG_WINDOW_SIZE: number = 650;
    protected properties: IMessageBoxViewModelConstructorParameter;
    private windowBuilder: WindowViewModelBuilder<this>;
    private imageBuilder: ImageViewModelBuilder<this>;
    private labelBuilder: LabelViewModelBuilder<this>;
    private dataBuilder: LabelViewModelBuilder<this>;
    private stackBuilder: LabelViewModelBuilder<this>;
    protected buttonBuilder: ButtonViewModelBuilder<this>[];

    public constructor(parent?: T) {
        super(parent);
        this.buttonBuilder = [];
    }

    public withWindow(): WindowViewModelBuilder<this> {
        if (this.windowBuilder == null)
            this.windowBuilder = new WindowViewModelBuilder<this>(this);

        return this.windowBuilder;
    }

    public withImage(): ImageViewModelBuilder<this> {
        if (this.imageBuilder == null)
            this.imageBuilder = new ImageViewModelBuilder<this>(this);

        return this.imageBuilder;
    }

    public withLabel(): LabelViewModelBuilder<this> {
        if (this.labelBuilder == null)
            this.labelBuilder = new LabelViewModelBuilder<this>(this);

        return this.labelBuilder;
    }

    public withData(): LabelViewModelBuilder<this> {
        if (this.dataBuilder == null)
            this.dataBuilder = new LabelViewModelBuilder<this>(this);

        return this.dataBuilder;
    }

    public withStack(): LabelViewModelBuilder<this> {
        if (this.stackBuilder == null)
            this.stackBuilder = new LabelViewModelBuilder<this>(this);

        return this.stackBuilder;
    }

    public withButton(): ButtonViewModelBuilder<this> {
        const builder = new ButtonViewModelBuilder<this>(this);
        this.buttonBuilder.push(builder);
        return builder;
    }

    public withStyle(style: string): this {
        this.properties.style = style;
        return this;
    }

    public initForExceptionInfoMessageBox(errorInformation: Dictionary<string>, caption: string, stack?: string, data?: object): this {
        this.initForWarningMessageBox(caption);

        if (RADApplication.getInstance().configuration.guiErrorLevel == 'None')
            return this;

        const oldButtons = this.buttonBuilder;
        this.buttonBuilder = [];

        const loc = 'error-message';

        if (stack) {
            this.withStack()
                .withParentLocator(loc)
                .withLocator('content.stack')
                .withIsVisible(true)
                .withCaption(stack)
                .done();
        }

        if (data) {
            this.withData()
                .withParentLocator(loc)
                .withLocator('content.data')
                .withIsVisible(true)
                .withCaption(JSON.stringify(data, null, 4))
                .done();
        }

        if (errorInformation) {
            this.withStyle('exception-info')
                .withButton()
                .withLabel()
                .withErgName('DOWNLOAD_EXCEPTIONINFO')
                .done()
                .withImage()
                .withSource(GlobalConfiguration.inlineImages.FILE_SAVE)
                .done()
                .withOnClick(_ => ExceptionInfoHelper.downloadExceptionInfo(errorInformation))
                .done();
        }

        this.buttonBuilder = this.buttonBuilder.concat(oldButtons);

        return this;
    }

    public initForWarningMessageBox(caption: ObservableOrDefault<string>, callback: Function = null, showCancelButton: boolean = false): this {
        const locator = 'warning-message';

        this.withWindow()    // WINDOW
            .updateProperties({
                parentLocator: locator,
                locator: 'window',
                width: this.MSG_WINDOW_SIZE,
                isModal: true,
                isOpen: true,
                allowClose: true,
                isMessageBox: true,
            })
            .withHeadlineErgName('WARNING_TITLE')
            .done()
            .withImage()    // IMAGE
            .updateProperties({
                parentLocator: locator,
                locator: 'content.image',
                source: 'message_warning.png',
            })
            .withAlternativeTextErgName('WARNING_TITLE')
            .done()
            .withLabel()    // LABEL
            .updateProperties({
                parentLocator: locator,
                locator: 'content.label',
                ergName: caption == null ? 'GENERIC_ERROR' : null,
                caption: caption
            })
            .done()
            .withButton()   // OK-Button
            .initForOkayButton(locator, () => {
                this.getBuild().window.isOpen(false);

                if (callback != null && Utilities.isFunction(callback)) {
                    callback(true);
                }
            });

        // Cancel-Button
        if (showCancelButton) {
            this.withButton()
                .initForCancelButton(locator, () => {
                    this.getBuild().window.isOpen(false);

                    if (callback != null && Utilities.isFunction(callback)) {
                        callback(false);
                    }
                })
                .done();
        }

        return this;
    }

    /**
     * @override
     */
    protected preBuild(): void {
        if (this.windowBuilder != null)
            this.properties.window = this.windowBuilder.build();

        if (this.imageBuilder != null)
            this.properties.image = this.imageBuilder.build();

        if (this.labelBuilder != null)
            this.properties.label = this.labelBuilder.build();

        if (this.dataBuilder != null)
            this.properties.data = this.dataBuilder.build();

        if (this.stackBuilder != null)
            this.properties.stack = this.stackBuilder.build();

        if (this.buttonBuilder != null)
            this.properties.buttons = this.buttonBuilder.map(x => x.build());
    }

    /**
     * @override
     * @inheritdoc 
     */
    protected buildInternal(): MessageBoxViewModelT {
        const MessageBoxViewModel = ComponentManager.getViewModel<typeof MessageBoxViewModelT>('MessageBoxViewModel');

        return new MessageBoxViewModel(this.properties);
    }
}